import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import {
  useEditOrganisationMutation,
  useGetOrganisationByIdQuery,
} from "../../services";
import { useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import { adminRoutes } from "../../routers/AdminRouters";

const OrganisationEdit: React.FC = () => {
  const { theme } = useTheme();
  const { orgid } = useParams<{ orgid: string }>();
  const navigate = useNavigate();

  const {
    data: organisation,
    isLoading,
    isError,
  } = useGetOrganisationByIdQuery(orgid!, {
    skip: !orgid,
  });

  const [editOrganisation, { isLoading: isUpdating }] =
    useEditOrganisationMutation();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    billing: "",
    city: "",
    state: "",
    country: "",
    postal: "",
  });

  // inline errors + email regex
  const [errors, setErrors] = useState<{ email?: string; phone?: string }>({});
  const emailRegex = /^\S+@\S+\.\S+$/;

  useEffect(() => {
    if (organisation) {
      setForm({
        name: organisation?.name || "",
        email: organisation?.email || "",
        phone: organisation?.phone || "",
        billing: organisation?.address || "",
        city: organisation?.city || "",
        state: organisation?.state || "",
        country: organisation?.country || "",
        postal: organisation?.postal || "",
      });
    }
  }, [organisation]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === "phone") {
      const cleaned = value.replace(/\D/g, "");
      setForm((f) => ({ ...f, phone: cleaned }));
      if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));

    if (name === "email" && errors.email) {
      setErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  const handleEmailBlur = () => {
    const email = form.email.trim();
    setErrors((prev) => ({
      ...prev,
      email: email && !emailRegex.test(email) ? "Email is invalid" : undefined,
    }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgid) return;

    try {
      await editOrganisation({
        orgid,
        data: {
          name: form.name,
          email: form.email,
          phone: form.phone,
          address: form.billing,
          city: form.city,
          state: form.state,
          country: form.country,
          postal: form.postal,
        },
      }).unwrap();

      navigate("/organisation");
    } catch (error) {
      console.error("Failed to update organisation:", error);
      alert("Failed to update organisation. Please try again.");
    }
  };

  const pageBg = theme === "dark" ? "bg-[#0B0B0B]" : "bg-gray-50";
  const cardClass =
    theme === "dark"
      ? "bg-[#1E1E1E] border border-[#2C2C2C] text-gray-100"
      : "bg-white border border-gray-200 text-gray-900";
  const inputClass =
    theme === "dark"
      ? "w-full rounded-md px-3 py-2 bg-[#111111] border border-[#2C2C2C] text-gray-100 placeholder-gray-500 outline-none focus:border-[#B6CB24]"
      : "w-full rounded-md px-3 py-2 bg-white border border-gray-300 text-gray-900 placeholder-gray-400 outline-none focus:border-[#B6CB24]";
  const labelClass =
    theme === "dark"
      ? "block text-sm mb-1 text-[#F3F3F6]"
      : "block text-sm mb-1 text-gray-700";

  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  if (isLoading) {
    return (
      <div className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}>
        <div className="flex items-center justify-center h-64">
          <p className={theme === "dark" ? "text-gray-300" : "text-gray-700"}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (isError || !organisation) {
    return (
      <div className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}>
        <div className="flex items-center justify-center h-64">
          <p className={theme === "dark" ? "text-red-400" : "text-red-600"}>
            Failed to load organisation details.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Breadcrumb + Title */}
      <div className="mb-4">
        <h1
          className={`text-xl font-semibold ${
            theme === "dark" ? "text-gray-100" : "text-gray-900"
          }`}
        >
          Edit Organisation
        </h1>
        <div className="text-sm mt-1">
          <Link
            to={`${adminRoutes.organisationList}`}
            className={theme === "dark" ? "text-gray-400" : "text-gray-500"}
          >
            All Organisations
          </Link>

          <span
            className={theme === "dark" ? "text-gray-600" : "text-gray-400"}
          >
            {" "}
            &nbsp;&gt;&nbsp;{" "}
          </span>
          <span className="text-[#B6CB24] font-medium">Edit Organisation</span>
        </div>
      </div>

      {/* Card */}
      <form
        onSubmit={onSubmit}
        className={`${cardClass} rounded-lg p-4 md:p-6`}
      >
        <h2 className="text-lg font-semibold mb-4 border-b pb-2 border-gray-700">
          Edit Organisation
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className={labelClass}>Organisation Name</label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Enter here"
              className={inputClass}
              required
            />
          </div>
          <div>
            <label className={labelClass}>Email</label>
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              onBlur={handleEmailBlur}
              placeholder="Enter here"
              className={`${inputClass} ${
                errors.email ? "border border-red-500" : ""
              }`}
              required
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>
          <div>
            <label className={labelClass}>Phone Number</label>
            <input
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="Enter here"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={15}
              className={`${inputClass} ${
                errors.phone ? "border border-red-500" : ""
              }`}
              required
            />
            {errors.phone && (
              <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
            )}
          </div>
        </div>
        <div className="mb-4">
          <label className={labelClass}>Billing address</label>
          <input
            name="billing"
            value={form.billing}
            onChange={handleChange}
            placeholder="Enter here"
            className={inputClass}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label className={labelClass}>City</label>
            <input
              name="city"
              value={form.city}
              disabled
              onChange={handleChange}
              placeholder="Enter here"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>State</label>
            <select
              name="state"
              value={form.state}
              onChange={handleChange}
              disabled
              className={inputClass}
            >
              <option value="">Choose from list</option>
              <option value="Tamil Nadu">Tamil Nadu</option>
              <option value="Kerala">Kerala</option>
              <option value="Karnataka">Karnataka</option>
              <option value="Maharashtra">Maharashtra</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Country</label>
            <select
              name="country"
              value={form.country}
              onChange={handleChange}
              className={inputClass}
              disabled
            >
              <option value="">Choose from list</option>
              <option value="India">India</option>
              <option value="UAE">UAE</option>
              <option value="USA">USA</option>
              <option value="UK">UK</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Postal / Zip code</label>
            <input
              name="postal"
              value={form.postal}
              onChange={handleChange}
              placeholder="Enter here"
              className={inputClass}
              disabled
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={isUpdating}
            className="px-8 py-1.5 rounded-md bg-[#B6CB24] text-black font-medium hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background:
                "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
            }}
          >
            {isUpdating ? "Updating..." : "Update"}
          </button>
          <Link
            to={`${adminRoutes.organisationList}`}
            className={`px-8 py-1.5 rounded-md font-medium ${
              theme === "dark"
                ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]"
                : "bg-gray-200 text-gray-800 hover:bg-gray-300"
            }`}
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
};

export default OrganisationEdit;
